import { isDesktopRuntime } from './desktopDetection';
import type { DesktopSessionReadType, IDesktopSessionCredentials } from './desktopSessionVault';

/**
 * T-0894 (`docs/concept-mobile-app.md`, LC-6g, its third part): the desktop's session vault and its
 * sign-in binding, loaded on the first call. A browser tab keeps its session in its own storage and
 * starts no desktop sign-in, so a file the first screen reaches asks here: outside the desktop
 * runtime each call answers what the vault answers there, without fetching it, and in the desktop
 * the module loads once and is called as before. `desktopEntryFree.guard.test.ts` keeps the vault
 * and the binding out of the entry.
 *
 * The desktop boot loads the vault before the first screen (`desktopBoot.ts`), and the session's
 * restore reads it through here, so the one synchronous question, `desktopSessionUnread`, is asked
 * of the module that did the read.
 */
type VaultType = typeof import('./desktopSessionVault');
type BindingType = typeof import('./desktopSignInBinding');

let vault: VaultType | null = null;
let vaultLoading: Promise<VaultType> | null = null;

/** The vault, loaded once. The desktop boot calls it so the first screen finds it loaded. */
export function loadDesktopSessionVault(): Promise<VaultType> {
  vaultLoading ??= import('./desktopSessionVault').then((module) => {
    vault = module;
    return module;
  });
  return vaultLoading;
}

/** Calls the vault at once when it is loaded, so writes keep the order they were made in. */
function withVault<TResult>(run: (module: VaultType) => Promise<TResult>): Promise<TResult> {
  return vault ? run(vault) : loadDesktopSessionVault().then(run);
}

/**
 * I-2572: whether the keychain's last read went unanswered. Only a read through the vault can leave
 * it unanswered, and that read loads it, so a vault not loaded yet has read nothing.
 */
export function desktopSessionUnread(): boolean {
  return vault?.desktopSessionUnread() ?? false;
}

export function readStoredDesktopSession(
  deploymentAddress: string,
  options: { onRetry?: () => void } = {},
): Promise<DesktopSessionReadType> {
  if (!isDesktopRuntime()) return Promise.resolve({ state: 'none' });
  return withVault((module) => module.readStoredDesktopSession(deploymentAddress, options));
}

export function writeDesktopSession(
  deploymentAddress: string,
  tokens: IDesktopSessionCredentials,
): Promise<void> {
  if (!isDesktopRuntime()) return Promise.resolve();
  return withVault((module) => module.writeDesktopSession(deploymentAddress, tokens));
}

export function writeDesktopSessionDurably(
  deploymentAddress: string,
  tokens: IDesktopSessionCredentials,
): Promise<boolean> {
  if (!isDesktopRuntime()) return Promise.resolve(true);
  return withVault((module) => module.writeDesktopSessionDurably(deploymentAddress, tokens));
}

export function clearDesktopSession(deploymentAddress: string): Promise<void> {
  if (!isDesktopRuntime()) return Promise.resolve();
  return withVault((module) => module.clearDesktopSession(deploymentAddress));
}

export function endDesktopSession(deploymentAddress: string): Promise<void> {
  if (!isDesktopRuntime()) return Promise.resolve();
  return withVault((module) => module.endDesktopSession(deploymentAddress));
}

/** The desktop's sign-in binding (I-1182), for a sign-in the desktop app starts or redeems. */
export function loadDesktopSignInBinding(): Promise<BindingType> {
  return import('./desktopSignInBinding');
}
